import {
  addCpfMask,
  formatTelephone,
  generateAvatarInitialsImage,
  getWhatsAppAPIURL,
  MainButton,
} from '@repo/ui';
import {
  GetAllPersonProfileUseCaseInterface,
  GetByIdPersonUseCaseInterface,
} from 'domain/usecases';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import FeatherIcon from 'feather-icons-react';
import { Col, Offcanvas, OffcanvasBody, Row } from 'reactstrap';

import { PersonDetailsOffcanvasSkeleton } from './person-details-offcanvas-skeleton';

import './styles.scss';
import { getEnv } from 'shared/get-env';

interface PersonData extends GetByIdPersonUseCaseInterface.Result {}

interface PersonProfilesData
  extends GetAllPersonProfileUseCaseInterface.Result {}

interface PersonDetailsOffcanvasProps {
  isOpen: boolean;
  toggle: () => void;
  onClosed: () => void;
  personData: PersonData | undefined;
  personProfilesData: PersonProfilesData | undefined;
  isLoading?: boolean;
  onViewProfileBtnClick: (personDetails: PersonData) => void;
}

export function PersonDetailsOffcanvas({
  isOpen,
  isLoading,
  personData,
  personProfilesData,
  toggle,
  onClosed,
  onViewProfileBtnClick,
}: PersonDetailsOffcanvasProps) {
  const profilesNames = personData?.profiles.map((profile) => profile.name);
  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      onClosed={onClosed}
      direction="end"
    >
      {isLoading && <PersonDetailsOffcanvasSkeleton />}

      {personData && personProfilesData && (
        <>
          <OffcanvasBody className="profile-offcanvas p-0">
            <div className="team-cover">
              <img
                src={getEnv('REACT_APP_ORGANIZATION_COVER_URL') || ''}
                alt="Cover do card da pessoa"
                className="img-fluid"
              />
            </div>
            <div className="p-3 text-center" style={{ marginTop: '2.5rem' }}>
              <img
                src={
                  personData.photo ||
                  generateAvatarInitialsImage(personData.name, 36)
                }
                alt="Foto da pessoa"
                className="avatar-lg object-fit-cover border border-4 rounded-circle border-success"
              />
              <div className="mt-3">
                <h5 className="fs-16 mb-2">{personData.name}</h5>
                <p className="text-muted mb-1">{addCpfMask(personData.cpf)}</p>
                <p className="text-muted mb-0">
                  {personData.profiles.length > 5
                    ? profilesNames.slice(0, 5).join(', ') + '...'
                    : profilesNames.join(', ')}
                </p>
              </div>
              <div className="hstack gap-2 justify-content-center mt-4">
                {personData.phone && (
                  <div className="avatar-xs hoverable">
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={getWhatsAppAPIURL(personData.phone)}
                      className="avatar-title bg-secondary-subtle text-success rounded fs-18"
                    >
                      <i className="ri-whatsapp-line"></i>
                    </a>
                  </div>
                )}
                {personData.email && (
                  <div className="avatar-xs hoverable">
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`mailto:${personData.email}`}
                      className="avatar-title bg-secondary-subtle text-secondary rounded fs-16"
                    >
                      <FeatherIcon size={16} icon="mail" />
                    </a>
                  </div>
                )}
              </div>
            </div>
            <Row className="g-0 text-center">
              <Col xs={6}>
                <div className="p-3 border border-dashed border-start-0">
                  <h5 className="mb-1">
                    {new Intl.NumberFormat('pt-BR').format(
                      personData.activeGroups || 0,
                    )}
                  </h5>
                  <p className="text-muted mb-0">Grupos Ativos</p>
                </div>
              </Col>
              <Col xs={6}>
                <div className="p-3 border border-dashed border-start-0">
                  <h5 className="mb-1">
                    {new Intl.NumberFormat('pt-BR').format(
                      personData.radinos || 0,
                    )}
                  </h5>
                  <p className="text-muted mb-0">Radinos</p>
                </div>
              </Col>
            </Row>
            <div className="p-3">
              <div className="mb-3">
                <p className="text-muted text-uppercase fw-semibold fs-12 mb-2">
                  Telefone
                </p>
                <h6>{formatTelephone(personData.phone) || '--'}</h6>
              </div>
              <div className="mb-3">
                <p className="text-muted text-uppercase fw-semibold fs-12 mb-2">
                  Email
                </p>
                <h6>{personData.email || '--'}</h6>
              </div>
              <div className="mb-3">
                <p className="text-muted text-uppercase fw-semibold fs-12 mb-2">
                  Localidade
                </p>
                <h6 className="mb-0">
                  {personData.city
                    ? `${personData.city?.name} - ${personData.city?.uf}`
                    : '--'}
                </h6>
              </div>
            </div>
            <div className="p-3 border-top">
              <h5 className="mb-3">Perfis</h5>
              {personProfilesData.length > 0 &&
                personProfilesData.map((personProfile, idx) => (
                  <div
                    key={personProfile.id}
                    className={`d-flex align-items-center ${personProfile.roleId === PROFILE_TYPE.STUDENT ? '' : 'hoverable cursor-pointer '} ${
                      idx === personProfilesData.length - 1 ? '' : 'mb-3'
                    }
                    `}
                    onClick={
                      personProfile.roleId === PROFILE_TYPE.STUDENT
                        ? undefined
                        : () => {
                            window.open(
                              `/impersonate/${personProfile.roleId === PROFILE_TYPE.DIRECTOR ? 'gestor' : 'organizacao'}/${personProfile.id}`,
                              '_blank',
                            );
                          }
                    }
                  >
                    {personProfile.roleId === PROFILE_TYPE.STUDENT ? (
                      <></>
                    ) : (
                      <div className="flex-shrink-0 avatar-xs">
                        <div className="avatar-title bg-primary-subtle text-primary rounded">
                          <FeatherIcon icon="lock" size={16} />
                        </div>
                      </div>
                    )}
                    <div
                      className={`flex-grow-1 ${personProfile.roleId === PROFILE_TYPE.STUDENT ? '' : 'ms-3'}`}
                    >
                      <h6 className="mb-1">{personProfile.entity.name}</h6>
                      <p className="text-muted mb-0">
                        {personProfile.course
                          ? personProfile.course.name
                          : '--'}
                      </p>
                    </div>
                    <div className="text-muted">{personProfile.name}</div>
                  </div>
                ))}
            </div>
          </OffcanvasBody>
          <div className="offcanvas-footer border p-3 hstack gap-3 text-center position-relative">
            <MainButton
              color="primary"
              onClick={() => onViewProfileBtnClick(personData)}
              className="w-100"
            >
              Ver Perfil
            </MainButton>
          </div>
        </>
      )}
    </Offcanvas>
  );
}
